<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const props = defineProps({
  transcription: {
    type: String,
    default: null,
  },
});

const {
  content,
  attachments,
  contentAttributes,
  messageType,
  conversationId,
  senderId,
  sender,
} = useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};

const isCopied = ref(false);
const copyToClipboard = () => {
  if (!content.value) return;
  navigator.clipboard.writeText(content.value);
  isCopied.value = true;
  setTimeout(() => {
    isCopied.value = false;
  }, 2000);
};

const locationData = computed(() => {
  if (!content.value) return null;

  // Formato Padronizado VIGICAM (Suporta Markdown: **Localização:** ou *Latitude:* etc)
  // Localização[:\*]* -> ignora estrelas e dois pontos
  // [\s\S]*? -> pula qualquer coisa entre os campos
  const strictMatch = content.value.match(
    /Localização[:\*]*[\s\S]*?Latitude[:\*]*\s*([-+]?\d+(?:\.\d+)?)\s*[\s\S]*?Longitude[:\*]*\s*([-+]?\d+(?:\.\d+)?)/i
  );

  if (strictMatch) {
    const lat = strictMatch[1];
    const lng = strictMatch[2];
    console.log('[VIGICAM] Localização padronizada detectada (Satélite):', {
      lat,
      lng,
    });
    return {
      lat,
      lng,
      embedUrl: `https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed&t=h`,
      googleMapsUrl: `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
    };
  }

  return null;
});

const contactData = computed(() => {
  if (!content.value) return null;

  // Formato Padronizado VIGICAM para Contatos
  // Suporta Markdown e novas linhas
  const contactMatch = content.value.match(
    /\*\*Contato:\*\*[\s\S]*?\*Nome:\*\s*([^\n*]+)[\s\S]*?\*Número\s*\(1\):\*\s*([^\n*|]+)/i
  );

  if (contactMatch) {
    const name = contactMatch[1].trim();
    const phone = contactMatch[2].trim();
    console.log('[VIGICAM] Contato padronizado detectado:', { name, phone });
    return { name, phone };
  }

  return null;
});

const handleAddContact = () => {
  if (!contactData.value) return;

  const payload = JSON.parse(
    JSON.stringify({
      event: 'chatwoot-contact-add-requested',
      data: {
        name: contactData.value.name,
        phone: contactData.value.phone,
        conversation_id: conversationId.value,
        contact_id: senderId.value || sender.value?.id,
      },
    })
  );

  window.parent.postMessage(payload, '*');
  console.log('[VIGICAM] Solicitação de adição de contato enviada:', payload);
};

const copyPhoneNumber = () => {
  if (!contactData.value) return;
  navigator.clipboard.writeText(contactData.value.phone);
  isCopied.value = true;
  setTimeout(() => {
    isCopied.value = false;
  }, 2000);
};
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <FormattedContent
        v-if="renderContent && !locationData && !contactData"
        :content="renderContent"
      />

      <!-- Card de Localização Vigicâmeras -->
      <div
        v-if="locationData"
        class="mt-2 group relative overflow-hidden rounded-xl border border-n-slate-3 bg-n-alpha-1 hover:border-n-brand-3 transition-all duration-300 shadow-sm"
      >
        <div class="h-40 w-full overflow-hidden relative">
          <iframe
            :src="locationData.embedUrl"
            width="100%"
            height="160"
            style="border: 0"
            allowfullscreen=""
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
            class="grayscale-[0.2] group-hover:grayscale-0 transition-all duration-700 scale-110"
          />
          <div
            class="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none"
          />
        </div>

        <div
          class="p-2 flex items-center justify-between bg-white dark:bg-n-slate-2 border-t border-n-slate-3"
        >
          <a
            :href="locationData.googleMapsUrl"
            target="_blank"
            class="text-[10px] font-bold text-n-brand-11 hover:text-branding-focus flex items-center gap-1.5 transition-colors uppercase tracking-tight"
          >
            <span class="i-ph-map-trifold-bold text-sm" />
            Abrir no Google Maps
          </a>

          <button
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-tight transition-all duration-200 active:scale-95"
            :class="
              isCopied
                ? 'bg-n-brand-3 text-n-brand-11'
                : 'bg-n-alpha-2 hover:bg-n-alpha-3 text-n-slate-11 hover:text-n-slate-12 border border-n-slate-3'
            "
            @click="copyToClipboard"
          >
            <span
              :class="isCopied ? 'i-ph-check-bold' : 'i-ph-copy-bold'"
              class="text-sm"
            />
            {{ isCopied ? 'Copiado!' : 'Copiar' }}
          </button>
        </div>
      </div>

      <!-- Card de Contato Vigicâmeras -->
      <div
        v-if="contactData"
        class="mt-2 group relative overflow-hidden rounded-xl border border-n-slate-3 bg-white dark:bg-n-slate-2 hover:border-n-brand-3 transition-all duration-300 shadow-sm p-4"
      >
        <div class="flex items-center gap-4 mb-4">
          <div
            class="w-12 h-12 rounded-full bg-n-brand-2 flex items-center justify-center text-n-brand-11 border border-n-brand-3 shadow-inner"
          >
            <span class="i-ph-user-circle-gear-bold text-3xl" />
          </div>
          <div class="flex flex-col min-w-0">
            <span class="text-sm font-bold text-n-slate-12 tracking-tight leading-tight">
              {{ contactData.name }}
            </span>
            <span class="text-xs font-semibold text-n-brand-11 mt-0.5">
              {{ contactData.phone }}
            </span>
          </div>
        </div>

        <div class="flex items-center gap-2 pt-3 border-t border-n-slate-3">
          <button
            class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-tight transition-all duration-200 active:scale-95 bg-n-alpha-2 hover:bg-n-alpha-3 text-n-slate-11 hover:text-n-slate-12 border border-n-slate-3"
            @click="copyPhoneNumber"
          >
            <span
              :class="isCopied ? 'i-ph-check-bold' : 'i-ph-copy-bold'"
              class="text-sm"
            />
            {{ isCopied ? 'Copiado!' : 'Copiar' }}
          </button>

          <button
            class="flex-[1.5] flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-tight transition-all duration-200 active:scale-95 bg-n-brand-3 hover:bg-n-brand-4 text-n-brand-11 border border-n-brand-4"
            @click="handleAddContact"
          >
            <span class="i-ph-user-plus-bold text-sm" />
            Adicionar Contato
          </button>
        </div>
      </div>

      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
